import { Injectable, OnDestroy } from '@angular/core';
import { raygun } from '@approot/app.raygun.setup';
import { AlertsService, AuthService, Token } from '@approot/services';
import {
  isFeatureEnabled,
  sessionId,
  shortUuid,
  sleep,
  SubscriptionSink,
  union
} from '@approot/utilities';
import { environment } from '@root/environments/environment';
import { from, NextObserver, Observable, Subscription, throwError } from 'rxjs';
import {
  catchError,
  filter,
  share,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { webSocket as rxjsWebSocket, WebSocketSubject } from 'rxjs/webSocket';
import {
  API_PREFIX_V2,
  ClientMessage,
  ServerMessage,
  SocketOptions,
  SubscriptionsResponsePayload,
  UnsubscribeRequestPayload
} from './types';

const ENDPOINT = environment.websocket;
const localDev = isFeatureEnabled('localDev', false);

const MAX_CONNECTION_ATTEMPTS = 10;

@Injectable({
  providedIn: 'root'
})
export class GlobalWebsocketService implements OnDestroy {
  private socket$: Observable<ServerMessage<any>>;
  private webSocket: WebSocketSubject<ClientMessage<any>>;
  private pingInterval: any;
  private pingSubscription: Subscription;
  private subsink = new SubscriptionSink();
  private unsubscribedChannels = new Set<string>();
  private currentChannels = new Set<string>();
  private resetInterval: any;
  private connectionAttempts = 0;
  private sessionId = sessionId();

  constructor(
    private authService: AuthService,
    private alertsService: AlertsService
  ) {
    setTimeout(async () => {
      await this.createWebsocket();
      await this.waitForSocket();
      this.keepAliveListener();
      this.startPing();
    });

    // AWS resets WS every 2 hours which generates a "false" error on Raygun
    // So we reset the socket a few mins early to try to avoid that.
    const resetInterval = (localDev ? 15 : 115) * 60 * 1000;
    this.resetInterval = setInterval(() => {
      this.webSocket?.complete();
    }, resetInterval);
  }

  /**
   * Should actually never happen, but just in case
   */
  public ngOnDestroy(): void {
    console.log('Destroying GlobalWebsocket service');
    this.webSocket?.complete();
    this.subsink.unsubscribe;
    clearInterval(this.pingInterval);
    clearInterval(this.resetInterval);
  }

  public getListener<T>(options?: SocketOptions): Observable<ServerMessage<T>> {
    return from(this.waitForSocket()).pipe(
      switchMap(() => {
        let sock = this.socket$; // .pipe(filter(m => this.filterSubscriptions(m)));

        let { action, uuid, take: numToTake = 1 } = options;

        if (action) {
          sock = sock.pipe(filter(m => action === m?.action));
        }
        if (uuid) {
          sock = sock.pipe(
            filter(m => uuid === m?.uuid),
            take(numToTake)
          );
        }
        return sock;
      })
    );
  }

  public genUUID() {
    return shortUuid();
  }

  public async sendMessage(
    message: ClientMessage<any>,
    options: { subscribeOnReconnect?: boolean } = {}
  ): Promise<string> {
    options = {
      subscribeOnReconnect: false,
      ...options
    };

    let { action, uuid } = message;
    if (!action.startsWith(API_PREFIX_V2)) {
      console.warn(
        `Send actions should probably start with ${API_PREFIX_V2}`,
        action
      );
    }

    const auth = await this.getJwtToken();

    if (!this.webSocket) {
      // this should probably be the last "await", because of a potential race
      await this.waitForSocket();
    }

    uuid = uuid ?? this.genUUID();

    this.webSocket.next({
      ...message,
      uuid,
      identity: { jwtToken: auth }
    });
    if (options.subscribeOnReconnect) {
      this.currentChannels.add(uuid);
    }
    return uuid;
  }

  /**
   *
   * @param uuid the uuid to unsubscribe from (don't confuse with the uuid of the message)
   * @returns
   */
  public async unsubscribe(uuid: string): Promise<void> {
    if (!uuid) {
      return;
    }
    this.unsubscribedChannels.add(uuid);
    this.currentChannels.delete(uuid);
    const msg: ClientMessage<UnsubscribeRequestPayload> = {
      action: `${API_PREFIX_V2}unsubscribe`,
      payload: { uuid }
    };
    await this.sendMessage(msg);
  }

  /*<PRIVATE>***************************************/

  private filterSubscriptions(
    message: ServerMessage<SubscriptionsResponsePayload>
  ) {
    switch (message.action) {
      case 'subscriptions':
        return false;
      default:
        return !this.unsubscribedChannels.has(message.uuid);
    }
  }

  // the socket is created async so need to ensure it exists
  private async waitForSocket(): Promise<void> {
    if (this.webSocket) {
      return;
    }

    let delta = 50;
    for (let i = 0; i < 100; i++) {
      await sleep(delta);
      if (this.webSocket) {
        return;
      }
    }
    if (!this.webSocket) {
      setTimeout(() => {
        if (!this.webSocket) {
          this.createWebsocket();
        }
      }, 1000);
      throw new Error('Socket not created');
    }
  }

  private async createWebsocket(): Promise<void> {
    this.connectionAttempts++;
    if (this.webSocket) {
      try {
        this.webSocket.observers.forEach(o => o.complete());
        this.webSocket.complete();
      } catch (e) {
        console.error('createWebsocket', e);
      }
    }
    this.socket$ = null;
    this.webSocket = null;

    if (this.connectionAttempts >= MAX_CONNECTION_ATTEMPTS) {
      if (this.connectionAttempts === MAX_CONNECTION_ATTEMPTS) {
        console.error(
          "Max connection attempts reached, can't connect to socket"
        );

        this.alertsService.addAlert({
          message:
            'Could not connect to server.  Please refresh the page and try again. [WS001]',
          config: {
            disableTimeOut: true,
            closeButton: false,
            tapToDismiss: false
          }
        });
        raygun('send', {
          error: 'Max websocket connection attempts reached',
          tags: ['Websocket']
        });
      }
      return;
    }
    console.log(
      `Creating GlobalWebsocket service [${this.connectionAttempts}]`
    );

    const auth = await this.getJwtToken();
    const authToken = encodeURIComponent(auth);
    const sessionToken = encodeURIComponent(this.sessionId);
    const url = `${ENDPOINT}?auth=${authToken}&sessionId=${sessionToken}`;
    const closeObserver = this.getCloseObserver();
    const openObserver = this.getOpenObserver();
    this.webSocket = rxjsWebSocket({ url, closeObserver, openObserver });
    this.resubscribeToChannels();
    this.socket$ = this.getSocketObservable().pipe(
      tap(message => {
        if (message.action === 'subscriptions') {
          if (message.payload.channels) {
            if (message.payload.channels?.length > 0) {
              const newChannels = new Set<string>(message.payload.channels);
              this.currentChannels = union(newChannels, this.currentChannels);
            }
          } else {
            // console.warn('No channels in websocket subscription', message);
          }
        }
      }),
      filter(message => this.filterSubscriptions(message))
    );
    this.connectionAttempts = 0;
  }

  private getCloseObserver(): NextObserver<CloseEvent> {
    return {
      next: (closeEvent: CloseEvent) => {
        console.warn('GlobalWebsocket closed', closeEvent);
        this.handleSocketError();
      }
    };
  }

  private getOpenObserver(): NextObserver<Event> {
    return {
      next: () => console.log('GlobalWebsocket opened')
    };
  }

  private async getJwtToken(): Promise<string> {
    let token = await this.authService.getJwtToken();
    return token;
  }

  private getSocketObservable(): Observable<ServerMessage<any>> {
    return this.webSocket.pipe(
      catchError(e => {
        if (e instanceof CloseEvent) {
          const message = `Socket closed by remote: ${e.reason}`;
          this.handleSocketError(message);
          return throwError(message);
        } else {
          this.handleSocketError(e);
          return throwError(e);
        }
      }),
      share()
    );
  }

  private handleSocketError(errorMessage?: string) {
    if (errorMessage) {
      console.warn(errorMessage);
    }
    this.createWebsocket();
  }

  private resubscribeToChannels() {
    if (this.currentChannels.size === 0) {
      return;
    }
    this.sendMessage({
      action: `${API_PREFIX_V2}subscribe`,
      payload: { channels: Array.from(this.currentChannels) }
    });
  }

  /**
   * RxJS closes the connection when there are no more subscriptions, so this keeps it alive
   */
  private keepAliveListener(sub?: Subscription) {
    sub?.unsubscribe();
    sub = this.getListener<any>({}).subscribe({
      complete: async () => {
        await sleep(1000);
        this.keepAliveListener(sub);
      }
    });

    this.subsink.sink = sub;
  }

  private startPing() {
    clearInterval(this.pingInterval);
    this.pingSubscription?.unsubscribe();

    // const pingInterval = isFeatureEnabled('localDev') ? 60_000 : 600_000;
    const pingInterval = 15_000;
    // const pingInterval = 11 * 60_000; // 11 minutes: AWS should timeout after 10 minutes of inactivity

    this.ping();
    this.pingInterval = setInterval(() => this.ping(), pingInterval);

    this.subsink.sink = this.pingSubscription;
  }

  private ping(): void {
    const msg: ClientMessage<{}> = {
      action: `${API_PREFIX_V2}subscribe`,
      payload: { channels: [] }
    };
    this.sendMessage(msg);
  }
}

/**
 * Deliberately mangle the token to make it invalid, specifically:
 *   a) exp, aud
 *   b) invalid signature
 *
 * NB, any change to (a) will automatically break the signature (b)
 *
 * @param token
 * @returns modified token
 */
function mungeToken(token: string) {
  let [header, payload, signature] = token.split('.');
  const p = JSON.parse(window.atob(payload)) as Token;
  // p.exp = Math.trunc(Date.now() / 1000 - 1);
  // p.aud = 'nosuchaudience';
  // signature = 'invalid';

  // Original urls are like "https:\/\/" so we need to restore that.  Since we
  // apply that globally, this is probably fragile
  payload = JSON.stringify(p).replace(/\//g, '\\/');
  const munged = `${header}.${btoa(payload)}.${signature}`;
  return munged;
}

function dumpToken(token: string) {
  let [_header, payload, _signature] = token.split('.');
  const p = JSON.parse(window.atob(payload)) as Token;
  console.log(
    'Token',
    `${JSON.stringify(p)}; exp: ${new Date(p.exp * 1000)}; iat: ${new Date(
      p.iat * 1000
    )}; auth: ${new Date(p['auth_time'] * 1000)}`
  );
}
